import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Globe } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/i18n'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/DropdownMenu'

const languageNames: Record<SupportedLanguage, string> = {
  it: 'Italiano',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  pl: 'Polski',
}

const languageCodes: Record<SupportedLanguage, string> = {
  it: 'IT',
  en: 'EN',
  de: 'DE',
  fr: 'FR',
  es: 'ES',
  pl: 'PL',
}

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
  isScrolled?: boolean
}

export function LanguageSelector({
  variant = 'desktop',
  isScrolled = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) return

    const pathSegments = location.pathname.split('/').filter(Boolean)
    const currentPath = pathSegments.slice(1).join('/')

    i18n.changeLanguage(newLang)
    navigate(`/${newLang}${currentPath ? `/${currentPath}` : ''}`)
  }

  if (variant === 'mobile') {
    return (
      <div className="flex items-center gap-2 px-4 py-3">
        <Globe className="h-5 w-5 text-neutral-500" />
        <div className="flex flex-wrap gap-2">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => switchLanguage(lang)}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded transition-colors',
                lang === currentLang
                  ? 'bg-primary-900 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              )}
            >
              {languageNames[lang]}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium transition-colors',
          isScrolled
            ? 'text-primary-900 hover:bg-neutral-100'
            : 'text-white hover:bg-white/10'
        )}
      >
        <Globe className="h-4 w-4" />
        <span>{languageCodes[currentLang]}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onClick={() => switchLanguage(lang)}
            className={cn(
              lang === currentLang && 'font-semibold bg-neutral-50'
            )}
          >
            <span className="mr-2 text-xs text-neutral-400">{languageCodes[lang]}</span>
            {languageNames[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
